import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@xxtract/ui';
import { useEvaluateProduct } from '../hooks/useDataQuality';

export interface EvaluateProductButtonProps {
  gln: string;
  cinXmlId: string;
}

/**
 * FR26: on-demand herberekening per product. The dialog always warns the score can differ
 * from the batch run (batch uses a generic rule baseline, on-demand uses per-product GPC
 * rules) before confirming, then — within the SAME dialog — replaces the confirm step with the
 * result once DQS responds, stating the rule basis actually used. The dialog is never
 * auto-closed on success: closing (and resetting mutation state) only happens on an explicit
 * user action, so the result can never be wiped out by its own success handler.
 */
export function EvaluateProductButton({ gln, cinXmlId }: EvaluateProductButtonProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { mutate, isPending, isSuccess, data, reset } = useEvaluateProduct(gln);

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) reset();
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <Button
        data-testid="evaluate-product-trigger"
        variant="outline"
        onClick={() => setOpen(true)}
      >
        {t('dataQuality.recalculate.button')}
      </Button>
      <DialogContent data-testid="evaluate-product-dialog">
        {isSuccess && data?.data ? (
          <>
            <DialogHeader>
              <DialogTitle>{t('dataQuality.recalculate.success')}</DialogTitle>
              <DialogDescription data-testid="evaluate-product-result">
                {t('dataQuality.recalculate.resultSummary', { score: data.data.overallScore, ruleBasis: data.data.ruleBasis })}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <DialogClose>
                <Button data-testid="evaluate-product-close">{t('common.close')}</Button>
              </DialogClose>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{t('dataQuality.recalculate.confirmTitle')}</DialogTitle>
              <DialogDescription data-testid="evaluate-product-warning">
                {t('dataQuality.recalculate.confirmMessage')}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <DialogClose>
                <Button variant="outline" data-testid="evaluate-product-cancel">{t('common.cancel')}</Button>
              </DialogClose>
              <Button
                data-testid="evaluate-product-confirm"
                disabled={isPending}
                onClick={() => mutate(cinXmlId)}
              >
                {isPending ? t('common.loading') : t('dataQuality.recalculate.confirmAction')}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
